import React, {
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  ReactNode,
  RefObject
} from 'react';
import { Loader } from '../Loader';

type RefProp<T> = RefObject<T | null> | null;

interface EndlessScrollProps {
  items: Readonly<{ length: number }>;
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefProp<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
  loadingPlaceholder?: ReactNode;
}

const EndlessScroll: React.FC<
  EndlessScrollProps & {
    Component: React.ComponentType<{ forwardedRef: RefProp<HTMLDivElement> }>;
  }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  items,
  Component,
  loadingPlaceholder,
  ...props
}) => {
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    requestAnimationFrame(() => {
      ticking.current = false;

      if (loading || isStopLoad) return;

      if (scrollContainer === 'parent') {
        const el = forwardedRef?.current;
        if (!el) return;

        const threshold = 150;
        const isNearBottom =
          el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

        if (isNearBottom) {
          onLoadMore();
        }

        return;
      }

      if (!forwardedRef?.current) return;

      const { top, height } = forwardedRef.current.getBoundingClientRect();
      const bottom = top + height;
      const offset = window.innerHeight;

      const hasScrolledPast = bottom < 0;
      const isWithinLoadDistance = bottom - offset <= window.innerHeight;

      if (!hasScrolledPast && isWithinLoadDistance) {
        onLoadMore();
      }
    });
  }, [onLoadMore, loading, isStopLoad, forwardedRef, scrollContainer]);

  useEffect(() => {
    const target =
      scrollContainer === 'parent' ? forwardedRef?.current : window;

    if (!target) return;

    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainer, forwardedRef]);

  const isLoadingMore = loading && !!items.length && !isStopLoad;

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />
      {isLoadingMore && (loadingPlaceholder ?? <Loader loading endless />)}
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll
      {...props}
      forwardedRef={typeof ref !== 'function' ? ref : null}
      Component={
        Component as React.ComponentType<{
          forwardedRef: RefProp<HTMLDivElement>;
        }>
      }
    />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
